import { captureMessage } from '@sentry/browser';
import React from 'react';
import { Panorama360VideoPlayer } from './Panorama360VideoPlayer';
import { getParameterByName } from './utils/getParameterByName';

export interface IPlayerState {
    assetId?: undefined | string;
    useHighBitrate: boolean;
    isSlowMotion: boolean;
    isPanorama: boolean;
}

export class Player extends React.Component<any, IPlayerState> {
    private videoElementHighRef: React.RefObject<HTMLVideoElement | null>;
    private videoElementLowRef: React.RefObject<HTMLVideoElement | null>;

    constructor(props: any) {
        super(props);
        this.videoElementHighRef = React.createRef<HTMLVideoElement | null>();
        this.videoElementLowRef = React.createRef<HTMLVideoElement | null>();

        this.state = {
            useHighBitrate: true,
            isSlowMotion: false,
            assetId: getParameterByName('assetid'),
            isPanorama: getParameterByName('panorama') === 'true',
        };
    }

    public override render() {
        if (!this.state.assetId) {
            return <span />;
        }

        if (this.state.isPanorama) {
            return (
                <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                        <Panorama360VideoPlayer
                            videoId="videoPanorama"
                            height="100%"
                            src={`/api/assets/${this.state.assetId}/download?downscaledHigh=true`}
                            onVideoElement={this.attachPanoramaVideoElement}
                            showPlayOverlay={false}
                            togglePlayOnClick={true}
                        />
                    </div>
                    <div className="p10">{this.renderControls()}</div>
                </div>
            );
        }

        return (
            <div style={{ height: '100vh' }}>
                <div className="admin-form mb10" style={{ height: '100vh' }}>
                    <div className="section p10" style={{ height: '100vh' }}>
                        <video
                            ref={this.videoElementLowRef}
                            id={'videolow'}
                            className={this.state.useHighBitrate ? 'hidden' : ''}
                            crossOrigin="use-credentials"
                            preload="auto"
                            controls={true}
                            height="85%"
                            muted
                        >
                            <source src={`/api/assets/${this.state.assetId}/download?downscaled=true`} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <video
                            ref={this.videoElementHighRef}
                            id={'videohigh'}
                            className={this.state.useHighBitrate ? '' : 'hidden'}
                            crossOrigin="use-credentials"
                            preload="metadata"
                            controls={true}
                            height="85%"
                            muted
                            onLoadedData={(e) => {
                                e.currentTarget.focus();
                            }}
                        >
                            <source src={`/api/assets/${this.state.assetId}/download?downscaledHigh=true`} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <div className="mt20">{this.renderControls()}</div>
                    </div>
                </div>
            </div>
        );
    }

    // Shared playback toolbar used by both the regular and the 360 panorama
    // player: slowmotion, 0.5s/1s/5s jump back and forward, and download.
    private renderControls() {
        return (
            <>
                <button
                    className={this.state.isSlowMotion ? 'hidden' : 'btn btn-default btn-sm mr10 fs12'}
                    onClick={() => this.onPlaybackRateChanged(0.5)}
                >
                    {' '}
                    <span className="fa fa-clock-o pr5"></span>Slowmotion
                </button>
                <button
                    className={this.state.isSlowMotion ? 'btn btn-success btn-sm mr10 fs12' : 'hidden'}
                    onClick={() => this.onPlaybackRateChanged(1)}
                >
                    {' '}
                    <span className="fa fa-clock-o pr5"></span> Slowmotion
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(-0.5)}>
                    {' '}
                    <span className="fa fa-undo pr5"></span>0.5
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(-1)}>
                    {' '}
                    <span className="fa fa-undo pr5"></span>1
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(-5)}>
                    {' '}
                    <span className="fa fa-undo pr5"></span>5
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(0.5)}>
                    {' '}
                    <span className="fa fa-repeat pr5"></span>0.5
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(1)}>
                    {' '}
                    <span className="fa fa-repeat pr5"></span>1
                </button>
                <button className="btn btn-primary btn-sm mr10 fs12" onClick={() => this.onVideoSetVideoJump(5)}>
                    {' '}
                    <span className="fa fa-repeat pr5"></span>5
                </button>
                <a
                    className="btn btn-primary btn-sm mr10 fs12"
                    href={`/api/assets/${this.state.assetId}/download?downscaledHigh=true&forceDownload=true`}
                    download
                >
                    {' '}
                    <span className="fa fa-download pr5"></span>Download
                </a>
            </>
        );
    }

    // PSV keeps the panorama <video> off-DOM (it is used purely as a WebGL
    // texture source), so React refs never attach to it. Point both bitrate refs
    // at the single forwarded panorama element so the existing slowmotion,
    // frame-by-frame and jump handlers operate on it directly. The video adapter
    // runs the viewer in continuous-update mode, so seeking refreshes the view
    // even while paused.
    private attachPanoramaVideoElement = (videoNode: HTMLVideoElement) => {
        this.videoElementHighRef.current = videoNode;
        this.videoElementLowRef.current = videoNode;
    };

    // Jump forward (positive value) or backward (negative value) by `value`
    // seconds. Both bitrate elements are kept in sync so switching quality keeps
    // the current time in place; for panorama both refs point at the same element.
    private onVideoSetVideoJump(value: number) {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }

        const target = Math.max(0, this.videoElementHighRef.current.currentTime + value);
        this.videoElementHighRef.current.currentTime = target;
        this.videoElementLowRef.current.currentTime = target;
    }

    private onPlaybackRateChanged(value: number) {
        if (!this.videoElementHighRef.current || !this.videoElementLowRef.current) {
            return;
        }

        if (value === 1) {
            this.setState({ isSlowMotion: false });
        } else {
            this.setState({ isSlowMotion: true });
            captureMessage('Using slow motion.');
        }

        this.videoElementHighRef.current.playbackRate = value;
        this.videoElementLowRef.current.playbackRate = value;
    }
}
